const en = {
  title: "Create your first video",
  description: "Describe one idea. AriaFlow will turn it into a complete 25-second faceless video.",
  topicLabel: "What should the video be about?",
  topicPlaceholder: "Example: Three habits that make mornings less stressful",
  languageLabel: "Video language",
  create: "Generate my video",
  creating: "Starting your video…",
  balance: "Current balance",
  estimatedCost: "Estimated use",
  credits: "credits",
  duration: "25 seconds",
  format: "9:16 vertical",
  production: "Script, voice, visuals and captions included",
  advanced: "Set up a full workflow instead",
  loading: "Preparing Quick Start…",
  invalidTopic: "Enter a clear topic between 3 and 500 characters.",
  genericError: "We could not start the video. Your topic is saved here; try again.",
  insufficientTitle: "More credits are needed",
  insufficientDescription: "Your balance is below the estimated cost. Top up before starting so generation is not interrupted.",
  buyCredits: "Buy credits",
  resumeTitle: "Your first video is already underway",
  resumeDescription: "Continue to the task page to see progress or recover a failed attempt.",
  resume: "Open generation progress",
  unavailable: "Quick Start is not available for this account.",
  privacy: "Nothing is published automatically. You can review and download the result first.",
};

export type ActivationQuickStartCopy = typeof en;

const copy: Record<string, ActivationQuickStartCopy> = {
  en,
  zh: {
    title: "生成你的第一条视频",
    description: "只需描述一个主题，AriaFlow 会把它制作成完整的 25 秒无脸视频。",
    topicLabel: "这条视频想讲什么？",
    topicPlaceholder: "例如：让早晨不再手忙脚乱的三个习惯",
    languageLabel: "视频语言",
    create: "生成我的视频",
    creating: "正在启动视频生成…",
    balance: "当前积分",
    estimatedCost: "预计使用",
    credits: "积分",
    duration: "25 秒",
    format: "9:16 竖屏",
    production: "包含文案、配音、画面和字幕",
    advanced: "改用完整工作流设置",
    loading: "正在准备快速生成…",
    invalidTopic: "请输入 3–500 个字符的清晰主题。",
    genericError: "暂时无法启动生成。主题仍保留在页面中，请重试。",
    insufficientTitle: "需要补充积分",
    insufficientDescription: "当前余额低于预计成本。请先补充积分，避免生成中断。",
    buyCredits: "购买积分",
    resumeTitle: "你的第一条视频已经开始生成",
    resumeDescription: "前往任务页查看进度，或恢复之前失败的生成。",
    resume: "查看生成进度",
    unavailable: "当前账户不适用快速生成。",
    privacy: "系统不会自动发布。你可以先查看并下载成片。",
  },
  "zh-tw": {
    title: "生成你的第一支影片",
    description: "只需描述一個主題，AriaFlow 會把它製作成完整的 25 秒無臉影片。",
    topicLabel: "這支影片想說什麼？",
    topicPlaceholder: "例如：讓早晨不再手忙腳亂的三個習慣",
    languageLabel: "影片語言",
    create: "生成我的影片",
    creating: "正在啟動影片生成…",
    balance: "目前點數",
    estimatedCost: "預計使用",
    credits: "點數",
    duration: "25 秒",
    format: "9:16 直式",
    production: "包含文案、配音、畫面和字幕",
    advanced: "改用完整工作流程設定",
    loading: "正在準備快速生成…",
    invalidTopic: "請輸入 3–500 個字元的清楚主題。",
    genericError: "目前無法啟動生成。主題仍保留在頁面中，請重試。",
    insufficientTitle: "需要補充點數",
    insufficientDescription: "目前餘額低於預估成本。請先補充點數，避免生成中斷。",
    buyCredits: "購買點數",
    resumeTitle: "你的第一支影片已經開始生成",
    resumeDescription: "前往任務頁查看進度，或恢復之前失敗的生成。",
    resume: "查看生成進度",
    unavailable: "目前帳戶不適用快速生成。",
    privacy: "系統不會自動發布。你可以先查看並下載成片。",
  },
  ja: {
    title: "最初の動画を作成",
    description: "テーマを1つ入力すると、AriaFlowが25秒の顔出し不要動画に仕上げます。",
    topicLabel: "動画のテーマは何ですか？",
    topicPlaceholder: "例：忙しい朝を楽にする3つの習慣",
    languageLabel: "動画の言語",
    create: "動画を生成",
    creating: "動画を開始しています…",
    balance: "現在のクレジット",
    estimatedCost: "推定使用量",
    credits: "クレジット",
    duration: "25秒",
    format: "9:16 縦型",
    production: "台本、音声、映像、字幕を含む",
    advanced: "詳細なワークフローを設定",
    loading: "クイックスタートを準備しています…",
    invalidTopic: "3〜500文字で明確なテーマを入力してください。",
    genericError: "動画を開始できませんでした。テーマは残っています。もう一度お試しください。",
    insufficientTitle: "クレジットが必要です",
    insufficientDescription: "残高が推定コストを下回っています。生成前にクレジットを追加してください。",
    buyCredits: "クレジットを購入",
    resumeTitle: "最初の動画はすでに進行中です",
    resumeDescription: "タスク画面で進行状況を確認するか、失敗した生成を再開できます。",
    resume: "生成状況を開く",
    unavailable: "このアカウントではクイックスタートを利用できません。",
    privacy: "自動公開はされません。まず結果を確認してダウンロードできます。",
  },
  ko: {
    title: "첫 번째 동영상 만들기",
    description: "주제 하나를 입력하면 AriaFlow가 완성된 25초 페이스리스 동영상으로 만듭니다.",
    topicLabel: "어떤 내용의 동영상을 만들까요?",
    topicPlaceholder: "예: 바쁜 아침을 편하게 만드는 세 가지 습관",
    languageLabel: "동영상 언어",
    create: "동영상 생성",
    creating: "동영상 생성을 시작하는 중…",
    balance: "현재 크레딧",
    estimatedCost: "예상 사용량",
    credits: "크레딧",
    duration: "25초",
    format: "9:16 세로형",
    production: "대본, 음성, 이미지, 자막 포함",
    advanced: "전체 워크플로 설정 사용",
    loading: "빠른 시작을 준비하는 중…",
    invalidTopic: "3~500자로 명확한 주제를 입력하세요.",
    genericError: "동영상을 시작하지 못했습니다. 주제는 유지되므로 다시 시도하세요.",
    insufficientTitle: "크레딧이 더 필요합니다",
    insufficientDescription: "잔액이 예상 비용보다 적습니다. 생성이 중단되지 않도록 먼저 충전하세요.",
    buyCredits: "크레딧 구매",
    resumeTitle: "첫 번째 동영상이 이미 진행 중입니다",
    resumeDescription: "작업 페이지에서 진행 상황을 확인하거나 실패한 생성을 복구하세요.",
    resume: "생성 진행 상황 열기",
    unavailable: "이 계정에서는 빠른 시작을 사용할 수 없습니다.",
    privacy: "자동으로 게시되지 않습니다. 결과를 먼저 확인하고 다운로드할 수 있습니다.",
  },
  de: {
    title: "Erstes Video erstellen",
    description: "Beschreibe eine Idee. AriaFlow erstellt daraus ein vollständiges 25-Sekunden-Video ohne Kamera.",
    topicLabel: "Worum soll es im Video gehen?",
    topicPlaceholder: "Beispiel: Drei Gewohnheiten für einen ruhigeren Morgen",
    languageLabel: "Videosprache",
    create: "Video generieren",
    creating: "Video wird gestartet…",
    balance: "Aktuelles Guthaben",
    estimatedCost: "Geschätzter Verbrauch",
    credits: "Credits",
    duration: "25 Sekunden",
    format: "9:16 Hochformat",
    production: "Skript, Stimme, Bilder und Untertitel inklusive",
    advanced: "Stattdessen vollständigen Workflow einrichten",
    loading: "Quick Start wird vorbereitet…",
    invalidTopic: "Gib ein klares Thema mit 3 bis 500 Zeichen ein.",
    genericError: "Das Video konnte nicht gestartet werden. Dein Thema bleibt erhalten. Versuche es erneut.",
    insufficientTitle: "Mehr Credits erforderlich",
    insufficientDescription: "Dein Guthaben liegt unter den geschätzten Kosten. Lade Credits auf, bevor du startest.",
    buyCredits: "Credits kaufen",
    resumeTitle: "Dein erstes Video ist bereits in Arbeit",
    resumeDescription: "Öffne die Aufgabe, um den Fortschritt zu sehen oder einen fehlgeschlagenen Versuch fortzusetzen.",
    resume: "Generierungsfortschritt öffnen",
    unavailable: "Quick Start ist für dieses Konto nicht verfügbar.",
    privacy: "Es wird nichts automatisch veröffentlicht. Du kannst das Ergebnis zuerst prüfen und herunterladen.",
  },
  fr: {
    title: "Créer votre première vidéo",
    description: "Décrivez une idée. AriaFlow la transforme en une vidéo sans visage complète de 25 secondes.",
    topicLabel: "Quel est le sujet de la vidéo ?",
    topicPlaceholder: "Exemple : trois habitudes pour des matins moins stressants",
    languageLabel: "Langue de la vidéo",
    create: "Générer ma vidéo",
    creating: "Lancement de la vidéo…",
    balance: "Solde actuel",
    estimatedCost: "Utilisation estimée",
    credits: "crédits",
    duration: "25 secondes",
    format: "Vertical 9:16",
    production: "Script, voix, visuels et sous-titres inclus",
    advanced: "Configurer plutôt un workflow complet",
    loading: "Préparation du démarrage rapide…",
    invalidTopic: "Saisissez un sujet clair de 3 à 500 caractères.",
    genericError: "Impossible de lancer la vidéo. Votre sujet reste ici ; réessayez.",
    insufficientTitle: "Crédits supplémentaires nécessaires",
    insufficientDescription: "Votre solde est inférieur au coût estimé. Rechargez avant de commencer.",
    buyCredits: "Acheter des crédits",
    resumeTitle: "Votre première vidéo est déjà en cours",
    resumeDescription: "Ouvrez la tâche pour suivre la progression ou reprendre une tentative échouée.",
    resume: "Voir la progression",
    unavailable: "Le démarrage rapide n’est pas disponible pour ce compte.",
    privacy: "Rien n’est publié automatiquement. Vous pouvez d’abord vérifier et télécharger le résultat.",
  },
  es: {
    title: "Crea tu primer vídeo",
    description: "Describe una idea. AriaFlow la convierte en un vídeo faceless completo de 25 segundos.",
    topicLabel: "¿De qué debe tratar el vídeo?",
    topicPlaceholder: "Ejemplo: tres hábitos para mañanas menos estresantes",
    languageLabel: "Idioma del vídeo",
    create: "Generar mi vídeo",
    creating: "Iniciando el vídeo…",
    balance: "Saldo actual",
    estimatedCost: "Uso estimado",
    credits: "créditos",
    duration: "25 segundos",
    format: "Vertical 9:16",
    production: "Incluye guion, voz, imágenes y subtítulos",
    advanced: "Configurar un workflow completo",
    loading: "Preparando el inicio rápido…",
    invalidTopic: "Escribe un tema claro de entre 3 y 500 caracteres.",
    genericError: "No pudimos iniciar el vídeo. El tema sigue aquí; inténtalo de nuevo.",
    insufficientTitle: "Se necesitan más créditos",
    insufficientDescription: "Tu saldo es inferior al coste estimado. Recarga antes de empezar.",
    buyCredits: "Comprar créditos",
    resumeTitle: "Tu primer vídeo ya está en marcha",
    resumeDescription: "Abre la tarea para ver el progreso o recuperar un intento fallido.",
    resume: "Abrir progreso",
    unavailable: "El inicio rápido no está disponible para esta cuenta.",
    privacy: "No se publica nada automáticamente. Puedes revisar y descargar el resultado primero.",
  },
  it: {
    title: "Crea il tuo primo video",
    description: "Descrivi un’idea. AriaFlow la trasforma in un video faceless completo di 25 secondi.",
    topicLabel: "Di cosa deve parlare il video?",
    topicPlaceholder: "Esempio: tre abitudini per mattine meno stressanti",
    languageLabel: "Lingua del video",
    create: "Genera il mio video",
    creating: "Avvio del video…",
    balance: "Saldo attuale",
    estimatedCost: "Utilizzo stimato",
    credits: "crediti",
    duration: "25 secondi",
    format: "Verticale 9:16",
    production: "Script, voce, immagini e sottotitoli inclusi",
    advanced: "Configura invece un workflow completo",
    loading: "Preparazione dell’avvio rapido…",
    invalidTopic: "Inserisci un argomento chiaro tra 3 e 500 caratteri.",
    genericError: "Impossibile avviare il video. L’argomento è ancora qui; riprova.",
    insufficientTitle: "Servono altri crediti",
    insufficientDescription: "Il saldo è inferiore al costo stimato. Ricarica prima di iniziare.",
    buyCredits: "Acquista crediti",
    resumeTitle: "Il tuo primo video è già in lavorazione",
    resumeDescription: "Apri l’attività per vedere i progressi o recuperare un tentativo fallito.",
    resume: "Apri avanzamento",
    unavailable: "L’avvio rapido non è disponibile per questo account.",
    privacy: "Nulla viene pubblicato automaticamente. Puoi prima controllare e scaricare il risultato.",
  },
  pt: {
    title: "Crie seu primeiro vídeo",
    description: "Descreva uma ideia. A AriaFlow transforma-a em um vídeo faceless completo de 25 segundos.",
    topicLabel: "Sobre o que será o vídeo?",
    topicPlaceholder: "Exemplo: três hábitos para manhãs menos estressantes",
    languageLabel: "Idioma do vídeo",
    create: "Gerar meu vídeo",
    creating: "Iniciando o vídeo…",
    balance: "Saldo atual",
    estimatedCost: "Uso estimado",
    credits: "créditos",
    duration: "25 segundos",
    format: "Vertical 9:16",
    production: "Roteiro, voz, imagens e legendas incluídos",
    advanced: "Configurar um workflow completo",
    loading: "Preparando o início rápido…",
    invalidTopic: "Digite um tema claro entre 3 e 500 caracteres.",
    genericError: "Não foi possível iniciar o vídeo. O tema continua aqui; tente novamente.",
    insufficientTitle: "São necessários mais créditos",
    insufficientDescription: "Seu saldo está abaixo do custo estimado. Recarregue antes de começar.",
    buyCredits: "Comprar créditos",
    resumeTitle: "Seu primeiro vídeo já está em andamento",
    resumeDescription: "Abra a tarefa para acompanhar ou recuperar uma tentativa com falha.",
    resume: "Abrir progresso",
    unavailable: "O início rápido não está disponível para esta conta.",
    privacy: "Nada é publicado automaticamente. Você pode revisar e baixar o resultado primeiro.",
  },
  nl: {
    title: "Maak je eerste video",
    description: "Beschrijf één idee. AriaFlow maakt er een complete faceless video van 25 seconden van.",
    topicLabel: "Waar moet de video over gaan?",
    topicPlaceholder: "Voorbeeld: drie gewoonten voor een rustigere ochtend",
    languageLabel: "Videotaal",
    create: "Mijn video genereren",
    creating: "Video wordt gestart…",
    balance: "Huidig saldo",
    estimatedCost: "Geschat gebruik",
    credits: "credits",
    duration: "25 seconden",
    format: "9:16 verticaal",
    production: "Script, stem, beelden en ondertitels inbegrepen",
    advanced: "In plaats daarvan een volledige workflow instellen",
    loading: "Quick Start voorbereiden…",
    invalidTopic: "Voer een duidelijk onderwerp van 3 tot 500 tekens in.",
    genericError: "De video kon niet worden gestart. Je onderwerp blijft staan; probeer opnieuw.",
    insufficientTitle: "Meer credits nodig",
    insufficientDescription: "Je saldo is lager dan de geschatte kosten. Vul eerst credits aan.",
    buyCredits: "Credits kopen",
    resumeTitle: "Je eerste video is al onderweg",
    resumeDescription: "Open de taak om de voortgang te bekijken of een mislukte poging te herstellen.",
    resume: "Voortgang openen",
    unavailable: "Quick Start is niet beschikbaar voor dit account.",
    privacy: "Er wordt niets automatisch gepubliceerd. Je kunt het resultaat eerst bekijken en downloaden.",
  },
  pl: {
    title: "Utwórz swój pierwszy film",
    description: "Opisz jeden pomysł. AriaFlow zmieni go w kompletny 25-sekundowy film bez twarzy.",
    topicLabel: "O czym ma być film?",
    topicPlaceholder: "Przykład: trzy nawyki na spokojniejszy poranek",
    languageLabel: "Język filmu",
    create: "Wygeneruj mój film",
    creating: "Uruchamianie filmu…",
    balance: "Bieżące saldo",
    estimatedCost: "Szacowane użycie",
    credits: "kredytów",
    duration: "25 sekund",
    format: "Pionowo 9:16",
    production: "Scenariusz, głos, obrazy i napisy w cenie",
    advanced: "Skonfiguruj pełny workflow",
    loading: "Przygotowywanie szybkiego startu…",
    invalidTopic: "Wpisz jasny temat o długości od 3 do 500 znaków.",
    genericError: "Nie udało się uruchomić filmu. Temat pozostał na stronie; spróbuj ponownie.",
    insufficientTitle: "Potrzeba więcej kredytów",
    insufficientDescription: "Saldo jest niższe od szacowanego kosztu. Uzupełnij kredyty przed startem.",
    buyCredits: "Kup kredyty",
    resumeTitle: "Twój pierwszy film jest już w trakcie tworzenia",
    resumeDescription: "Otwórz zadanie, aby sprawdzić postęp lub wznowić nieudaną próbę.",
    resume: "Otwórz postęp",
    unavailable: "Szybki start nie jest dostępny dla tego konta.",
    privacy: "Nic nie zostanie opublikowane automatycznie. Najpierw możesz sprawdzić i pobrać wynik.",
  },
  ru: {
    title: "Создайте первое видео",
    description: "Опишите одну идею. AriaFlow превратит её в готовое 25-секундное видео без съёмки.",
    topicLabel: "О чём должно быть видео?",
    topicPlaceholder: "Например: три привычки для спокойного утра",
    languageLabel: "Язык видео",
    create: "Создать видео",
    creating: "Запускаем создание видео…",
    balance: "Текущий баланс",
    estimatedCost: "Ожидаемый расход",
    credits: "кредитов",
    duration: "25 секунд",
    format: "Вертикально 9:16",
    production: "Сценарий, голос, изображения и субтитры включены",
    advanced: "Настроить полный процесс",
    loading: "Подготавливаем быстрый старт…",
    invalidTopic: "Введите понятную тему длиной от 3 до 500 символов.",
    genericError: "Не удалось запустить видео. Тема сохранена на странице; попробуйте ещё раз.",
    insufficientTitle: "Нужно больше кредитов",
    insufficientDescription: "Баланс ниже ожидаемой стоимости. Пополните его перед запуском.",
    buyCredits: "Купить кредиты",
    resumeTitle: "Ваше первое видео уже создаётся",
    resumeDescription: "Откройте задачу, чтобы увидеть прогресс или восстановить неудачную попытку.",
    resume: "Открыть прогресс",
    unavailable: "Быстрый старт недоступен для этого аккаунта.",
    privacy: "Ничего не публикуется автоматически. Сначала можно проверить и скачать результат.",
  },
};

export function resolveActivationQuickStartCopy(
  locale: unknown,
): ActivationQuickStartCopy {
  const normalized = String(locale || "en").toLowerCase();
  return copy[normalized] || copy[normalized.split("-")[0]] || en;
}

export const ACTIVATION_QUICK_START_COPY_LOCALES = Object.keys(copy);

const repeatEn = {
  title: "Create another video",
  description: "Keep the same 25-second format and production settings. Just choose the next topic.",
  topicLabel: "Next video topic",
  topicPlaceholder: "Example: Three ways to stay focused in the afternoon",
  create: "Generate next video",
  creating: "Starting next video…",
};

export type ActivationRepeatCopy = typeof repeatEn;

const repeatCopy: Record<string, ActivationRepeatCopy> = {
  en: repeatEn,
  zh: { title: "继续生成第二条视频", description: "沿用相同的 25 秒格式和制作设置，只需输入下一个主题。", topicLabel: "下一条视频主题", topicPlaceholder: "例如：下午保持专注的三个方法", create: "生成下一条视频", creating: "正在启动下一条视频…" },
  "zh-tw": { title: "繼續生成第二支影片", description: "沿用相同的 25 秒格式和製作設定，只需輸入下一個主題。", topicLabel: "下一支影片主題", topicPlaceholder: "例如：下午保持專注的三個方法", create: "生成下一支影片", creating: "正在啟動下一支影片…" },
  ja: { title: "次の動画を作成", description: "同じ25秒の形式と制作設定を使い、次のテーマだけを入力します。", topicLabel: "次の動画のテーマ", topicPlaceholder: "例：午後も集中力を保つ3つの方法", create: "次の動画を生成", creating: "次の動画を開始しています…" },
  ko: { title: "다음 동영상 만들기", description: "같은 25초 형식과 제작 설정을 유지하고 다음 주제만 입력하세요.", topicLabel: "다음 동영상 주제", topicPlaceholder: "예: 오후에도 집중력을 유지하는 세 가지 방법", create: "다음 동영상 생성", creating: "다음 동영상을 시작하는 중…" },
  de: { title: "Weiteres Video erstellen", description: "Nutze dasselbe 25-Sekunden-Format und dieselben Einstellungen. Wähle nur das nächste Thema.", topicLabel: "Thema des nächsten Videos", topicPlaceholder: "Beispiel: Drei Wege zu mehr Fokus am Nachmittag", create: "Nächstes Video generieren", creating: "Nächstes Video wird gestartet…" },
  fr: { title: "Créer une autre vidéo", description: "Conservez le même format de 25 secondes et les mêmes réglages. Choisissez simplement le prochain sujet.", topicLabel: "Sujet de la prochaine vidéo", topicPlaceholder: "Exemple : trois façons de rester concentré l’après-midi", create: "Générer la prochaine vidéo", creating: "Lancement de la prochaine vidéo…" },
  es: { title: "Crea otro vídeo", description: "Mantén el mismo formato de 25 segundos y los mismos ajustes. Solo elige el siguiente tema.", topicLabel: "Tema del siguiente vídeo", topicPlaceholder: "Ejemplo: tres formas de concentrarse por la tarde", create: "Generar siguiente vídeo", creating: "Iniciando el siguiente vídeo…" },
  it: { title: "Crea un altro video", description: "Mantieni lo stesso formato di 25 secondi e le stesse impostazioni. Scegli solo il prossimo argomento.", topicLabel: "Argomento del prossimo video", topicPlaceholder: "Esempio: tre modi per restare concentrati nel pomeriggio", create: "Genera il prossimo video", creating: "Avvio del prossimo video…" },
  pt: { title: "Crie outro vídeo", description: "Mantenha o mesmo formato de 25 segundos e as mesmas configurações. Escolha apenas o próximo tema.", topicLabel: "Tema do próximo vídeo", topicPlaceholder: "Exemplo: três formas de manter o foco à tarde", create: "Gerar próximo vídeo", creating: "Iniciando o próximo vídeo…" },
  nl: { title: "Maak nog een video", description: "Gebruik dezelfde 25-secondenindeling en productie-instellingen. Kies alleen het volgende onderwerp.", topicLabel: "Onderwerp van de volgende video", topicPlaceholder: "Voorbeeld: drie manieren om 's middags gefocust te blijven", create: "Volgende video genereren", creating: "Volgende video wordt gestart…" },
  pl: { title: "Utwórz kolejny film", description: "Zachowaj ten sam 25-sekundowy format i ustawienia produkcji. Wybierz tylko kolejny temat.", topicLabel: "Temat kolejnego filmu", topicPlaceholder: "Przykład: trzy sposoby na skupienie po południu", create: "Wygeneruj kolejny film", creating: "Uruchamianie kolejnego filmu…" },
  ru: { title: "Создайте ещё одно видео", description: "Сохраните тот же 25-секундный формат и настройки. Выберите только следующую тему.", topicLabel: "Тема следующего видео", topicPlaceholder: "Например: три способа сохранять концентрацию после обеда", create: "Создать следующее видео", creating: "Запускаем следующее видео…" },
};

export function resolveActivationRepeatCopy(locale: unknown): ActivationRepeatCopy {
  const normalized = String(locale || "en").toLowerCase();
  return repeatCopy[normalized] || repeatCopy[normalized.split("-")[0]] || repeatEn;
}
